'use client';

import { useState } from 'react';

import type { ProductInfo } from '@/entities/content';
import {
  colorName,
  colorTint,
  defaultColorId,
  defaultSize,
  findVariant,
  selectColor,
  selectSize,
  type Product,
  type Selection,
} from '@/entities/product';
import { AccordionItem } from '@/shared/ui/accordion';
import { MediaFrame } from '@/shared/ui/media-frame';
import { ProductGallery } from '@/widgets/product-gallery';
import { ProductPurchase } from '@/widgets/product-purchase';

import styles from './product-view.module.scss';

export type ProductDetailsProps = {
  product: Product;
  looks: Product[];
  info: Pick<ProductInfo, 'delivery' | 'returns'>;
  initialColorId: string | null;
  initialFitOpen: boolean;
  showDemoNotes: boolean;
};

/** Gallery, purchase aside and "О товаре" share one colour/size selection. */
export function ProductDetails({
  product,
  looks,
  info,
  initialColorId,
  initialFitOpen,
  showDemoNotes,
}: ProductDetailsProps) {
  const [selection, setSelection] = useState<Selection>(() => ({
    colorId:
      initialColorId && product.colors.some((color) => color.id === initialColorId)
        ? initialColorId
        : defaultColorId(product),
    size: defaultSize(product),
  }));
  const [tip, setTip] = useState('');

  const onColorChange = (colorId: string) => {
    const result = selectColor(product, selection, colorId);
    setSelection(result.selection);
    setTip(result.tip);
    if (result.selection.colorId !== selection.colorId && result.selection.colorId) {
      // Keep the colour shareable without a server round trip.
      window.history.replaceState(
        null,
        '',
        `?color=${encodeURIComponent(result.selection.colorId)}`,
      );
    }
  };
  const onSizeChange = (size: string) => {
    const result = selectSize(product, selection, size);
    setSelection(result.selection);
    setTip(result.tip);
  };

  const variant = findVariant(product, selection);
  const color = colorName(product, selection.colorId);
  // 03B shows the article of the chosen colour ("[артикул · Графит]").
  const article =
    product.colors.find((item) => item.id === selection.colorId)?.article ??
    variant?.sku ??
    product.variants[0]?.sku ??
    '—';

  return (
    <>
      <section className={styles.top}>
        <ProductGallery
          product={product}
          colorId={selection.colorId}
          size={product.sizes.length > 1 ? selection.size : null}
          preorder={variant?.availability === 'preorder'}
          showDemoNotes={showDemoNotes}
        />
        <ProductPurchase
          product={product}
          looks={looks}
          selection={selection}
          tip={tip}
          onColorChange={onColorChange}
          onSizeChange={onSizeChange}
          onTip={setTip}
          initialFitOpen={initialFitOpen}
        />
      </section>
      <section className={styles.about} aria-labelledby="about-product">
        <MediaFrame
          src={null}
          alt=""
          tint={colorTint(product, selection.colorId)}
          sizes="50vw"
          className={styles.detail}
          placeholder={{
            label: 'Фото · О товаре',
            sub: `крупный кадр товара${color ? ` · ${color}` : ''}`,
          }}
        />
        <div className={styles.info}>
          <h2 id="about-product" className={styles.h2}>
            О товаре
          </h2>
          <dl className={styles.specs}>
            <div className={styles.spec}>
              <dt>Артикул</dt>
              <dd>{article}</dd>
            </div>
            {product.specs.map((spec) => (
              <div key={spec.label} className={styles.spec}>
                <dt>{spec.label}</dt>
                <dd>{spec.value}</dd>
              </div>
            ))}
          </dl>
          <div className={styles.accordions}>
            <AccordionItem title="Доставка">
              <p className={styles.panel}>{info.delivery}</p>
            </AccordionItem>
            <AccordionItem
              title="Возврат"
              headerClassName={styles['last-trigger']}
              panelClassName={styles['last-panel']}
            >
              <p className={styles.panel}>{info.returns}</p>
            </AccordionItem>
          </div>
        </div>
      </section>
    </>
  );
}
